import React, { useContext, useRef, useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-apollo';
import gql from 'graphql-tag';
import UserContext from '../../pages/Login/UserContext';
import { createUser, signUpGuestUser, login, mailResetPassword } from '../../graphql/mutations';
import './RegisterMachine.scss';
import { track } from '../../utils/mixpanel';
import { mixpanelIdentifyUser } from '../../utils/mixpanelIdentifyUser';
import InputField from './InputField';
import client from '../../GraphQLClient';

const RegisterMachine = ({ onSuccess, initialMode = 'register', onModeChange, isBrightMode = false }) => {
  const { t } = useTranslation();
  const userContext = useContext(UserContext) || {};
  const { user, saveUserToLocalStorage, setAuthData } = userContext;

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mode, setMode] = useState(initialMode); // 'register' | 'login' | 'forgot'
  const [emailSent, setEmailSent] = useState(false);
  
  // Notify parent component when mode changes
  useEffect(() => {
    if (onModeChange) {
      onModeChange(mode);
    }
  }, [mode, onModeChange]);
  const [error, setError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [emailFocusedTracked, setEmailFocusedTracked] = useState(false);
  const [passwordFocusedTracked, setPasswordFocusedTracked] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const googleButtonRef = useRef(null);
  const renderCountRef = useRef(0);
  const emailInputRef = useRef(null);
  const passwordInputRef = useRef(null);
  const previousModeRef = useRef(mode);

  // Debug: Track renders and check input values
  useEffect(() => {
    renderCountRef.current += 1;
    console.log('🔵 [RegisterMachine] Component rendered, count:', renderCountRef.current, {
      email,
      passwordLength: password.length,
      mode,
      isSubmitting
    });
    // Check if input DOM values match React state
    if (emailInputRef.current) {
      const domValue = emailInputRef.current.value;
      if (domValue !== email) {
        console.warn('⚠️ [RegisterMachine] Email DOM value mismatch!', {
          reactState: email,
          domValue: domValue,
          'DOM === React': domValue === email
        });
      }
    }
    if (passwordInputRef.current) {
      const domValue = passwordInputRef.current.value;
      if (domValue !== password) {
        console.warn('⚠️ [RegisterMachine] Password DOM value mismatch!', {
          reactStateLength: password.length,
          domValueLength: domValue.length,
          'DOM === React': domValue === password
        });
      }
    }
  });

  // Debug: Track state changes
  useEffect(() => {
    console.log('🔵 [RegisterMachine] Email state changed:', email);
  }, [email]);

  useEffect(() => {
    console.log('🔵 [RegisterMachine] Password state changed:', password ? '***' : '(empty)');
  }, [password]);

  // Handler functions with console logs - NOT using useCallback to avoid stale closures
  const handleEmailChange = (e) => {
    const newValue = e.target.value;
    console.log('🔵 [RegisterMachine] Email onChange fired:', {
      oldValue: email,
      newValue: newValue,
      eventType: e.type,
      targetValue: e.target.value,
      inputElement: e.target,
      timestamp: Date.now()
    });
    console.log('🔵 [RegisterMachine] Calling setEmail with:', newValue);
    setEmail(newValue);
    setError('');
    setEmailError(''); // Clear email-specific error when user types
  };

  const handlePasswordChange = (e) => {
    const newValue = e.target.value;
    console.log('🔵 [RegisterMachine] Password onChange fired:', {
      oldValueLength: password.length,
      newValueLength: newValue.length,
      eventType: e.type,
      targetValue: e.target.value,
      inputElement: e.target,
      timestamp: Date.now()
    });
    console.log('🔵 [RegisterMachine] Calling setPassword with length:', newValue.length);
    setPassword(newValue);
    setError('');
    setPasswordError(''); // Clear password-specific error when user types
  };

  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => { isMountedRef.current = false; };
  }, []);

  const [signUpGuestUserMutation] = useMutation(gql(signUpGuestUser));
  const [signUpNewUserMutation] = useMutation(gql(createUser));
  const [loginMutation] = useMutation(gql(login));
  const [mailResetPasswordMutation] = useMutation(gql(mailResetPassword));

  const handleGoogleSignInCallback = useCallback(async (response) => {
    try {
      console.log('🔵 [Frontend] Google Sign-In callback received');
      console.log('🔵 [Frontend] Response:', {
        credential: response.credential ? `${response.credential.substring(0, 50)}...` : 'missing',
        select_by: response.select_by,
        client_id: response.client_id
      });
      
      try { track('APP_Felt_Google_SignIn_Attempt', { path: window.location.pathname }); } catch {}
      
      // Send credential to backend for verification
      // Use localhost backend for development, production URL for production
      // For local development, always use localhost backend
      const isLocalDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
      const backendUrl = isLocalDev 
        ? 'http://localhost:8080'  // Always use local backend when running locally
        : (process.env.REACT_APP_BACKEND_URL || 'https://felt-webapp.herokuapp.com');
      
      console.log('🔵 [Frontend] Sending credential to backend:', `${backendUrl}/auth/google/verify`);
      console.log('🔵 [Frontend] Backend URL from env:', process.env.REACT_APP_BACKEND_URL);
      console.log('🔵 [Frontend] Is local dev:', isLocalDev);
      console.log('🔵 [Frontend] Using backend URL:', backendUrl);
      console.log('⚠️  Note: For local dev, using localhost:8080. Make sure your backend is running!');
      
      // Get guest user token if available (for transferring guest data)
      const guestToken = localStorage.getItem('token');
      const headers = {
        'Content-Type': 'application/json',
      };
      if (guestToken && user?.isGuest) {
        headers['Authorization'] = `Bearer ${guestToken}`;
        console.log('🔵 [Frontend] Sending guest user token for data transfer');
      }
      
      const result = await fetch(`${backendUrl}/auth/google/verify`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify({ credential: response.credential }),
      });

      console.log('🔵 [Frontend] Backend response status:', result.status);
      
      if (!result.ok) {
        // Check if response is JSON or HTML (404 pages return HTML)
        const contentType = result.headers.get('content-type');
        let errorData;
        
        if (contentType && contentType.includes('application/json')) {
          errorData = await result.json();
        } else {
          // Response is HTML (likely a 404 page)
          const text = await result.text();
          console.error('❌ [Frontend] Backend returned HTML instead of JSON:', text.substring(0, 200));
          throw new Error(`Backend endpoint not found (${result.status}). Make sure /auth/google/verify is deployed.`);
        }
        
        console.error('❌ [Frontend] Backend error response:', errorData);
        const errorMessage = errorData.error || errorData.details || 'Google sign-in failed';
        
        // Provide more helpful error messages
        if (errorMessage.includes('audience') || errorMessage.includes('Invalid token')) {
          throw new Error('Google authentication failed. Please try again or use email/password.');
        }
        
        throw new Error(errorMessage);
      }

      const contentType = result.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await result.text();
        console.error('❌ [Frontend] Backend returned non-JSON response:', text.substring(0, 200));
        throw new Error('Backend returned invalid response format');
      }

      const data = await result.json();
      console.log('✅ [Frontend] Backend success response:', {
        hasToken: !!data.token,
        userId: data.user?.id,
        userEmail: data.user?.email,
        userName: data.user?.name,
        hasUserProgress: !!data.user?.userProgress,
        userProgressKeys: data.user?.userProgress ? Object.keys(data.user.userProgress).length : 0
      });
      
      if (data.token && saveUserToLocalStorage && setAuthData) {
        console.log('🔵 [Frontend] Saving user data to localStorage');
        saveUserToLocalStorage(data.token, data.user.id);
        
        // Small delay to ensure token is saved before making GraphQL request
        await new Promise(resolve => setTimeout(resolve, 100));
        
        // Refresh user data from database to ensure we have the latest progress
        try {
          console.log('🔵 [Frontend] Refreshing user data from database...');
          const token = localStorage.getItem('token');
          console.log('🔵 [Frontend] Token available for validateToken:', !!token);
          
          const validateTokenQuery = {
            query: gql`
              query ValidateToken {
                validateToken {
                  id
                  email
                  locale
                  adultRole
                  isRealFelter
                  isFeltOnboardingComplete
                  members { id name ageRange gender animal }
                  subscription {
                    id
                    plan_id
                    status
                    lastPayment
                    nextBillingTime
                  }
                  userProgress
                  userFeedback
                  userLog
                  playtime
                  exitPoll {
                    subscriptionLikelihood
                    reasons
                    explanation
                  }
                  type
                  isGuest
                  is1stFeedbackComplete
                  isOnboardingComplete
                  isFeltOnboardingComplete
                  name
                }
              }
            `
          };
          
          const refreshResult = await client.query({
            ...validateTokenQuery,
            fetchPolicy: 'network-only', // Always fetch fresh data from server
            context: {
              headers: {
                authorization: token ? `Bearer ${token}` : ''
              }
            }
          });
          
          if (refreshResult?.data?.validateToken) {
            const refreshedUser = refreshResult.data.validateToken;
            const refreshedProgressKeys = refreshedUser.userProgress ? Object.keys(refreshedUser.userProgress).length : 0;
            const googleProgressKeys = data.user?.userProgress ? Object.keys(data.user.userProgress).length : 0;
            
            console.log('✅ [Frontend] User data refreshed from database');
            console.log('🔵 [Frontend] Refreshed UserProgress:', refreshedProgressKeys > 0 ? `Present (${refreshedProgressKeys} keys)` : 'Missing');
            console.log('🔵 [Frontend] Google response UserProgress:', googleProgressKeys > 0 ? `Present (${googleProgressKeys} keys)` : 'Missing');
            
            // Always prefer refreshed data from validateToken (it's the source of truth)
            // But merge in progress from Google response if refreshed data is missing it
            let finalUserData = refreshedUser;
            if (refreshedProgressKeys === 0 && googleProgressKeys > 0) {
              console.log('⚠️ [Frontend] Refreshed data missing progress, merging from Google response');
              finalUserData = {
                ...refreshedUser,
                userProgress: data.user.userProgress
              };
            }
            console.log('✅ [Frontend] Using final user data with progress keys:', finalUserData.userProgress ? Object.keys(finalUserData.userProgress).length : 0);
            setAuthData(finalUserData);
            mixpanelIdentifyUser(finalUserData);
          } else {
            // Fallback to data from Google sign-in response
            console.log('⚠️ [Frontend] validateToken returned no data, using Google sign-in response');
            console.log('🔵 [Frontend] Google response UserProgress:', data.user?.userProgress ? `Present (${Object.keys(data.user.userProgress).length} keys)` : 'Missing');
            setAuthData(data);
            if (data.user) {
              mixpanelIdentifyUser(data.user);
            }
          }
        } catch (refreshError) {
          console.error('⚠️ [Frontend] Failed to refresh user data:', refreshError);
          console.error('⚠️ [Frontend] Error details:', {
            message: refreshError.message,
            graphQLErrors: refreshError.graphQLErrors,
            networkError: refreshError.networkError
          });
          console.log('🔵 [Frontend] Falling back to Google sign-in response');
          const googleProgressKeys = data.user?.userProgress ? Object.keys(data.user.userProgress).length : 0;
          console.log('🔵 [Frontend] Google response UserProgress:', googleProgressKeys > 0 ? `Present (${googleProgressKeys} keys)` : 'Missing');
          if (googleProgressKeys > 0) {
            console.log('✅ [Frontend] Using Google response with progress');
          } else {
            console.warn('⚠️ [Frontend] Google response also missing progress - this may indicate a backend issue');
          }
          // Fallback to data from Google sign-in response
          setAuthData(data);
          if (data.user) {
            mixpanelIdentifyUser(data.user);
          }
        }
        
        try { track('APP_Felt_Google_SignIn_Success', { path: window.location.pathname }); } catch {}
        console.log('✅ [Frontend] Google Sign-In completed successfully');
        onSuccess && onSuccess({ email: data.user.email });
      } else {
        console.error('❌ [Frontend] Missing token or user data in response');
        throw new Error(data.error || 'Google sign-in failed - missing token');
      }
    } catch (err) {
      console.error('❌ [Frontend] Google Sign-In error:', err);
      console.error('❌ [Frontend] Error details:', {
        message: err.message,
        stack: err.stack
      });
      
      // Provide user-friendly error messages
      let errorMsg = err?.message || 'Error signing in with Google';
      
      // Map technical errors to user-friendly messages
      if (errorMsg.includes('audience') || errorMsg.includes('Invalid token') || errorMsg.includes('401')) {
        errorMsg = t('loginFlow.googleAuthFailed') || 'Google authentication failed. Please try again or use email/password.';
        console.error('⚠️ [Frontend] Client ID mismatch detected. Frontend uses REACT_APP_GOOGLE_CLIENT_ID, backend uses OAUTH_CLIENT_ID. They must match!');
      } else if (errorMsg.includes('403') || errorMsg.includes('origin')) {
        errorMsg = t('loginFlow.googleAuthConfigError') || 'Google Sign-In is not configured for this domain. Please use email/password.';
      }
      
      if (isMountedRef.current) {
        setError(errorMsg);
        setEmailError(''); // Clear email error, show general error
        setPasswordError(''); // Clear password error, show general error
      }
      try { track('APP_Felt_Google_SignIn_Error', { message: String(errorMsg).slice(0, 200) }); } catch {}
    }
  }, [saveUserToLocalStorage, setAuthData, onSuccess]);

  // Initialize Google Sign-In button - memoize to prevent re-initialization
  const initGoogleSignInRef = useRef(false);
  
  // Reset initialization flag when mode changes
  useEffect(() => {
    // Reset when switching modes (especially between register/login)
    // This allows the button to be re-rendered with correct text (signup_with vs signin_with)
    if (previousModeRef.current !== mode) {
      initGoogleSignInRef.current = false;
      // Clear the button container when switching modes
      if (googleButtonRef.current) {
        googleButtonRef.current.innerHTML = '';
      }
      previousModeRef.current = mode;
    }
    // Reset emailSent when switching modes
    if (mode !== 'forgot') {
      setEmailSent(false);
    }
  }, [mode]);
  
  useEffect(() => {
    if ((mode !== 'register' && mode !== 'login') || !googleButtonRef.current || initGoogleSignInRef.current) return;

    const initGoogleSignIn = () => {
      if (initGoogleSignInRef.current) return; // Already initialized
      initGoogleSignInRef.current = true;
      if (window.google && window.google.accounts) {
        // Use the client ID from frontend .env
        const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
        
        if (!clientId) {
          console.error('Google Client ID is not configured. Please set REACT_APP_GOOGLE_CLIENT_ID in .env');
          console.error('Also make sure your current origin is added to Authorized JavaScript origins in Google Cloud Console');
          return;
        }

        // Log current origin for debugging
        const currentOrigin = window.location.origin;
        console.log('🔵 [Frontend] Google Sign-In Initialization:');
        console.log('  - Current Origin:', currentOrigin);
        console.log('  - Full URL:', window.location.href);
        console.log('  - Client ID:', clientId ? `${clientId.substring(0, 20)}...` : 'NOT SET');
        console.log('  - Client ID Full:', clientId);
        console.log('  - REACT_APP_GOOGLE_CLIENT_ID:', process.env.REACT_APP_GOOGLE_CLIENT_ID ? `${process.env.REACT_APP_GOOGLE_CLIENT_ID.substring(0, 20)}...` : 'NOT SET');
        console.log('  - REACT_APP_OAUTH_CLIENT_ID:', process.env.REACT_APP_OAUTH_CLIENT_ID ? `${process.env.REACT_APP_OAUTH_CLIENT_ID.substring(0, 20)}...` : 'NOT SET');
        console.log('');
        console.log('⚠️  IMPORTANT: If you see 403 errors, add this EXACT origin to Google Cloud Console:');
        console.log('    →', currentOrigin);
        console.log('    → Go to: APIs & Services → Credentials → Edit OAuth Client');
        console.log('    → Add to "Authorized JavaScript origins"');
        console.log('');

        window.google.accounts.id.initialize({
          client_id: clientId,
          callback: handleGoogleSignInCallback,
        });
        
        // Clear any existing button first
        if (googleButtonRef.current) {
          googleButtonRef.current.innerHTML = '';
        }
        
        window.google.accounts.id.renderButton(
          googleButtonRef.current,
          {
            type: 'standard',
            theme: 'filled_blue',
            size: 'large',
            text: mode === 'register' ? 'signup_with' : 'signin_with',
            // width: 400, // Let CSS handle width instead
          }
        );
      }
    };

    // Check if Google script is already loaded
    if (window.google && window.google.accounts) {
      initGoogleSignIn();
    } else {
      // Wait for Google script to load
      const checkGoogle = setInterval(() => {
        if (window.google && window.google.accounts) {
          clearInterval(checkGoogle);
          initGoogleSignIn();
        }
      }, 100);

      // Cleanup after 10 seconds
      setTimeout(() => clearInterval(checkGoogle), 10000);
    }
  }, [mode, handleGoogleSignInCallback]);

  // Helper function to parse and translate errors
  const parseError = useCallback((err, mode) => {
    console.log('🔵 [RegisterMachine] Parsing error:', err);
    
    // Check for network errors
    if (!err || (!err.message && !err.graphQLErrors && !err.networkError)) {
      return t('loginFlow.networkError') || 'Network error. Please check your connection and try again.';
    }
    
    // GraphQL errors
    if (err.graphQLErrors && err.graphQLErrors.length > 0) {
      const gqlError = err.graphQLErrors[0];
      const errorMessage = gqlError.message || '';
      
      // Map backend error messages to translations
      if (errorMessage.includes('User already exists') || errorMessage.includes('already exists')) {
        return t('loginFlow.userAlreadyExists') || 'An account with this email already exists.';
      }
      if (errorMessage.includes('Invalid user')) {
        return t('loginFlow.invalidUser') || 'Invalid user. Please try again.';
      }
      if (errorMessage.includes('not found') && mode === 'login') {
        return t('loginFlow.userNotFound') || 'No account found with this email.';
      }
      if (errorMessage.includes('Invalid password') || errorMessage.includes('Wrong password')) {
        return t('loginFlow.invalidPassword') || 'Incorrect password. Please try again.';
      }
      if (errorMessage.includes('email was not found') && mode === 'forgot') {
        return t('loginFlow.emailNotFound') || 'No account found with this email.';
      }
      
      // Return the GraphQL error message if no translation found
      return errorMessage;
    }
    
    // Network errors
    if (err.networkError) {
      const networkErr = err.networkError;
      if (networkErr.statusCode === 0 || networkErr.message?.includes('NetworkError') || networkErr.message?.includes('Failed to fetch')) {
        return t('loginFlow.networkError') || 'Network error. Please check your connection and try again.';
      }
      if (networkErr.statusCode === 500) {
        return t('loginFlow.serverError') || 'Server error. Please try again later.';
      }
      return networkErr.message || t('loginFlow.networkError') || 'Network error. Please try again.';
    }
    
    // Regular error messages
    const errorMessage = err.message || String(err);
    
    // Map common error messages
    if (errorMessage.includes('User already exists') || errorMessage.includes('already exists')) {
      return t('loginFlow.userAlreadyExists') || 'An account with this email already exists.';
    }
    if (errorMessage.includes('Invalid user')) {
      return t('loginFlow.invalidUser') || 'Invalid user. Please try again.';
    }
    if (errorMessage.includes('not found') && mode === 'login') {
      return t('loginFlow.userNotFound') || 'No account found with this email.';
    }
    if (errorMessage.includes('Invalid password') || errorMessage.includes('Wrong password')) {
      return t('loginFlow.invalidPassword') || 'Incorrect password. Please try again.';
    }
    if (errorMessage.includes('email was not found') && mode === 'forgot') {
      return t('loginFlow.emailNotFound') || 'No account found with this email.';
    }
    
    // Return the error message as-is if no translation found
    return errorMessage || t('loginFlow.unknownError') || 'An error occurred. Please try again.';
  }, [t]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    
    // Clear all errors
    setError('');
    setEmailError('');
    setPasswordError('');
    
    // Validate required fields
    const trimmedEmail = email.trim();
    const trimmedPassword = password.trim();
    
    let hasValidationError = false;
    
    if (!trimmedEmail) {
      setEmailError(t('loginFlow.emailRequired') || 'Email is required');
      hasValidationError = true;
    } else {
      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(trimmedEmail)) {
        setEmailError(t('loginFlow.emailNotValid') || 'Please enter a valid email address');
        hasValidationError = true;
      }
    }
    
    if (mode === 'register' || mode === 'login') {
      if (!trimmedPassword) {
        setPasswordError(t('loginFlow.passwordRequired') || 'Password is required');
        hasValidationError = true;
      } else if (trimmedPassword.length < 6) {
        // Validate password length (minimum 6 characters)
        setPasswordError(t('loginFlow.passwordTooShort') || 'Password must be at least 6 characters');
        hasValidationError = true;
      }
    }
    
    if (hasValidationError) {
      setIsSubmitting(false);
      return;
    }
    
    setIsSubmitting(true);
    try {
      if (mode === 'register') {
        try { track('APP_Felt_Register_Submit_Attempt', { path: window.location.pathname }); } catch {}
        const mutation = user?.isGuest ? signUpGuestUserMutation : signUpNewUserMutation;
        const resultKey = user?.isGuest ? 'signUpGuestUser' : 'createUser';
        const result = await mutation({ variables: { userInput: { email: trimmedEmail, password: trimmedPassword } } });
        const resultData = result.data[resultKey];
        const { token, user: createdUser } = resultData;
        if (saveUserToLocalStorage && setAuthData) {
          saveUserToLocalStorage(token, createdUser.id);
          setAuthData(resultData);
        }
        mixpanelIdentifyUser(createdUser);
        try { track('APP_Felt_Register_Success', { path: window.location.pathname }); } catch {}
        onSuccess && onSuccess({ email: trimmedEmail });
      } else if (mode === 'login') {
        try { track('APP_Felt_Login_Submit_Attempt', { path: window.location.pathname }); } catch {}
        const result = await loginMutation({ variables: { email: trimmedEmail, password: trimmedPassword } });
        const resultData = result?.data?.login;
        if (resultData && saveUserToLocalStorage && setAuthData) {
          saveUserToLocalStorage(resultData.token, resultData.id);
          setAuthData(resultData);
        }
        if (resultData?.user) {
          mixpanelIdentifyUser(resultData.user);
        }
        try { track('APP_Felt_Login_Success'); } catch {}
        onSuccess && onSuccess({ email: trimmedEmail });
      } else if (mode === 'forgot') {
        await mailResetPasswordMutation({ variables: { email: trimmedEmail } });
        try { track('APP_Felt_ForgotPassword_Sent'); } catch {}
        setEmailSent(true);
        setError(''); // Clear error on success
      }
    } catch (err) {
      console.error('❌ [RegisterMachine] Submit error:', err);
      const errorMessage = parseError(err, mode);
      
      // Determine which field the error belongs to
      const errorMsg = errorMessage.toLowerCase();
      if (errorMsg.includes('email') || errorMsg.includes('user not found') || errorMsg.includes('email not found') || errorMsg.includes('already exists')) {
        setEmailError(errorMessage);
        setError(''); // Clear general error since this is email-specific
        setPasswordError(''); // Clear password error since this is email-specific
      } else if (errorMsg.includes('password') || errorMsg.includes('invalid password') || errorMsg.includes('wrong password')) {
        setPasswordError(errorMessage);
        setError(''); // Clear general error since this is password-specific
        setEmailError(''); // Clear email error since this is password-specific
      } else {
        // General error (network, server, etc.)
        setError(errorMessage);
        setEmailError(''); // Clear field-specific errors for general errors
        setPasswordError('');
      }
      try { track('APP_Felt_Auth_Error', { message: String(errorMessage).slice(0, 200), mode, errorType: err?.graphQLErrors ? 'graphql' : err?.networkError ? 'network' : 'unknown' }); } catch {}
    } finally {
      if (isMountedRef.current) setIsSubmitting(false);
    }
  };


  const renderContent = () => {
    switch (mode) {
      case 'login':
        return (
          <>
            {mode === 'login' && (
              <>
                <div ref={googleButtonRef} className="RegisterMachine__google-container"></div>
                <div className="RegisterMachine__or-separator">
                  <div className="RegisterMachine__or-line"></div>
                  <span className="RegisterMachine__or-text">{t('general.or')}</span>
                  <div className="RegisterMachine__or-line"></div>
                </div>
              </>
            )}
            <InputField
              type="email"
              label={t('loginFlow.email')}
              value={email}
              onChange={handleEmailChange}
              onFocus={() => {
                if (!emailFocusedTracked) {
                  try { track('APP_Felt_Register_Email_Focus'); } catch {}
                  setEmailFocusedTracked(true);
                }
              }}
              error={emailError}
              required
              isBrightMode={isBrightMode}
            />
            <InputField
              type="password"
              label={t('loginFlow.password')}
              value={password}
              onChange={handlePasswordChange}
              onFocus={() => {
                if (!passwordFocusedTracked) {
                  try { track('APP_Felt_Register_Password_Focus'); } catch {}
                  setPasswordFocusedTracked(true);
                }
              }}
              error={passwordError}
              required
              showPasswordToggle={true}
              showPassword={showPassword}
              onTogglePassword={setShowPassword}
              isBrightMode={isBrightMode}
            />
            {error && !emailError && !passwordError && (
              <div className="RegisterMachine__error">{error}</div>
            )}
            <button type="submit" className="RegisterMachine__submit" disabled={isSubmitting}>
              {isSubmitting ? t('general.loading') || 'Loading...' : t('loginFlow.logInBtn')}
            </button>
            <div className="RegisterMachine__footer-separator-line"></div>
            <div className="RegisterMachine__footer">
              <button type="button" className="RegisterMachine__footer-link" onClick={() => { 
                setMode('forgot'); 
                setEmailSent(false);
                setError(''); 
                setEmailError(''); 
                setPasswordError(''); 
                onModeChange && onModeChange('forgot');
              }}>
                {t('loginFlow.forgotPassword')}
              </button>
              <span className="RegisterMachine__footer-separator">|</span>
              <button type="button" className="RegisterMachine__footer-link" onClick={() => { 
                setMode('register'); 
                setError(''); 
                setEmailError(''); 
                setPasswordError(''); 
                onModeChange && onModeChange('register');
              }}>
                {t('loginFlow.createAccount')}
              </button>
            </div>
          </>
        );
      case 'register':
        return (
          <>
          <div className="RegisterMachine__age-restriction">
              {(() => {
                const fullText = t('loginFlow.ageRestriction');
                
                // Hebrew: "למדיניות הפרטיות" and "לתנאי השימוש"
                // English: "the Privacy Policy" and "Terms of Service"
                // Arabic: "سياسة الخصوصية" and "شروط الخدمة"
                // Russian: "Политикой конфиденциальности" and "Условиями использования"
                
                // Try to find privacy policy phrase - look for common patterns
                const privacyPatterns = [
                  'מדיניות הפרטיות',
                  'Privacy Policy',
                  'سياسة الخصوصية',
                  'Политикой конфиденциальности',
                  'Политику конфиденциальности'
                ];
                
                const termsPatterns = [
                  'תנאי השימוש',
                  'Terms of Service',
                  'شروط الخدمة',
                  'Условиями использования',
                  'Условия использования'
                ];
                
                let privacyIndex = -1;
                let privacyPattern = '';
                for (const pattern of privacyPatterns) {
                  const index = fullText.indexOf(pattern);
                  if (index !== -1) {
                    privacyIndex = index;
                    privacyPattern = pattern;
                    break;
                  }
                }
                
                let termsIndex = -1;
                let termsPattern = '';
                for (const pattern of termsPatterns) {
                  const index = fullText.indexOf(pattern);
                  if (index !== -1 && index > privacyIndex) {
                    termsIndex = index;
                    termsPattern = pattern;
                    break;
                  }
                }
                
                if (privacyIndex !== -1 && termsIndex !== -1) {
                  const beforePrivacy = fullText.substring(0, privacyIndex);
                  const privacyText = fullText.substring(privacyIndex, privacyIndex + privacyPattern.length);
                  const betweenText = fullText.substring(privacyIndex + privacyPattern.length, termsIndex);
                  const termsText = fullText.substring(termsIndex, termsIndex + termsPattern.length);
                  const afterTerms = fullText.substring(termsIndex + termsPattern.length);
                  
                  return (
                    <>
                      {beforePrivacy}
                      <a className="RegisterMachine__age-restriction-link" href="https://catbears.com/privacy" target="_blank" rel="noopener noreferrer">{privacyText}</a>
                      {betweenText}
                      <a className="RegisterMachine__age-restriction-link" href="https://catbears.com/terms" target="_blank" rel="noopener noreferrer">{termsText}</a>
                      {afterTerms}
                    </>
                  );
                }
                
                // Fallback: return text as-is if parsing fails
                return fullText;
              })()}
            </div>
          {mode === 'register' && (
              <>
                <div ref={googleButtonRef} className="RegisterMachine__google-container"></div>
                <div className="RegisterMachine__or-separator">
                  <div className="RegisterMachine__or-line"></div>
                  <span className="RegisterMachine__or-text">{t('general.or')}</span>
                  <div className="RegisterMachine__or-line"></div>
                </div>
              </>
            )}
            <InputField
              type="email"
              label={t('loginFlow.email')}
              value={email}
              onChange={handleEmailChange}
              onFocus={() => {
                if (!emailFocusedTracked) {
                  try { track('APP_Felt_Register_Email_Focus'); } catch {}
                  setEmailFocusedTracked(true);
                }
              }}
              error={emailError}
              required
              isBrightMode={isBrightMode}
            />
            <InputField
              type="password"
              label={t('loginFlow.password')}
              value={password}
              onChange={handlePasswordChange}
              onFocus={() => {
                if (!passwordFocusedTracked) {
                  try { track('APP_Felt_Register_Password_Focus'); } catch {}
                  setPasswordFocusedTracked(true);
                }
              }}
              error={passwordError}
              required
              showPasswordToggle={true}
              showPassword={showPassword}
              onTogglePassword={setShowPassword}
              isBrightMode={isBrightMode}
            />
            {error && !emailError && !passwordError && (
              <div className="RegisterMachine__error">{error}</div>
            )}
            <button type="submit" className="RegisterMachine__submit" disabled={isSubmitting}>
              {isSubmitting ? t('general.loading') || 'Loading...' : t('loginFlow.createAnAccount')}
            </button>
            
            <div className="RegisterMachine__footer-separator-line"></div>
            <div className="RegisterMachine__footer">
              <button type="button" className="RegisterMachine__footer-link" onClick={() => { 
                setMode('login'); 
                setError(''); 
                setEmailError(''); 
                setPasswordError(''); 
                onModeChange && onModeChange('login');
              }}>
                <span className="RegisterMachine__footer-text">{t('loginFlow.alreadyAccount')} </span>
                <span className="RegisterMachine__footer-link-text">{t('loginFlow.logIn')}</span>
              </button>
            </div>
          </>
        );
      case 'forgot':
        if (emailSent) {
          return (
            <>
              <div className="RegisterMachine__email-sent">
                <div className="RegisterMachine__email-sent-title">{t('modal.reset-password.title')}</div>
                <div className="RegisterMachine__email-sent-message">{t('modal.reset-password.subtitle')}</div>
              </div>
              <button 
                type="button" 
                className="RegisterMachine__submit" 
                onClick={() => { 
                  setMode('login'); 
                  setEmailSent(false);
                  setError(''); 
                  setEmailError(''); 
                  setPasswordError(''); 
                  onModeChange && onModeChange('login');
                }}
              >
                {t('modal.reset-password.backToLogin') || t('loginFlow.logInBtn')}
              </button>
            </>
          );
        }
        return (
          <>
            <InputField
              type="email"
              label={t('loginFlow.email')}
              value={email}
              onChange={handleEmailChange}
              onFocus={() => {
                if (!emailFocusedTracked) {
                  try { track('APP_Felt_Register_Email_Focus'); } catch {}
                  setEmailFocusedTracked(true);
                }
              }}
              error={emailError}
              required
              isBrightMode={isBrightMode}
            />
            {error && !emailError && (
              <div className="RegisterMachine__error">{error}</div>
            )}
            <button type="submit" className="RegisterMachine__submit" disabled={isSubmitting}>
              {isSubmitting ? t('general.loading') || 'Loading...' : t('general.send')}
            </button>
            <div className="RegisterMachine__footer-separator-line"></div>
            <div className="RegisterMachine__footer">
              <button type="button" className="RegisterMachine__footer-link" onClick={() => { 
                setMode('login'); 
                setError(''); 
                setEmailError(''); 
                setPasswordError(''); 
                onModeChange && onModeChange('login');
              }}>
                <span className="RegisterMachine__footer-text">{t('loginFlow.rememberPassword')} </span>
                <span className="RegisterMachine__footer-link-text">{t('loginFlow.logIn')}</span>
              </button>
            </div>
          </>
        );
      default:
        return null;
    }
  };

  return (
    <div className={`RegisterMachine ${isBrightMode ? 'RegisterMachine--bright' : ''}`}>
      <form onSubmit={handleSubmit} noValidate className="RegisterMachine__form">
        {renderContent()}
      </form>
    </div>
  );
};

export default RegisterMachine;

